import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import {
  Dimension,
  DrawingStyling,
  ElementOverrides,
  Template,
  TemplateElement
} from '@/models/drawing/drawingTypes.ts'
import { getFontDefinitionById } from '@/models/fontDetails.ts'

const DIMENSION_A5: Dimension = {
  width: 1748,
  height: 2480,
}

export interface DrawingCanvasHandle {
  setDimensions: (dimensions: { width: number; height: number }) => void
  getDimensions: () => { width: number; height: number }
}

interface DrawingCanvasProps {
  template: Template
  zoom?: number
  elementOverrides?: ElementOverrides
}

const DrawingCanvas = forwardRef<DrawingCanvasHandle, DrawingCanvasProps>(
  ({ template, zoom = 0.9, elementOverrides }, ref) => {
    const cardRef = useRef<HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [cardDimensions, setCardDimensions] = useState({ width: 0, height: 0 })

    // Expose methods to parent
    useImperativeHandle(ref, () => ({
      setDimensions: (dimensions: { width: number; height: number }) => {
        setCardDimensions(dimensions)
      },
      getDimensions: () => cardDimensions,
    }))

    useEffect(() => {
      const resizeCard = () => {
        if (!containerRef.current) return

        const containerWidth = containerRef.current.clientWidth
        const containerHeight = containerRef.current.clientHeight

        // Skip if container has no size yet
        if (containerWidth === 0 || containerHeight === 0) {
          return
        }

        // Calculate the scale to fit the A5 card in the container
        const aspectRatio = DIMENSION_A5.width / DIMENSION_A5.height

        let width, height

        // Check if we should scale based on width or height
        if (containerWidth / containerHeight > aspectRatio) {
          // Container is wider - scale based on height
          height = containerHeight * zoom
          width = height * aspectRatio
        } else {
          // Container is taller - scale based on width
          width = containerWidth * zoom
          height = width / aspectRatio
        }

        setCardDimensions({ width, height })
      }

      resizeCard()

      const resizeObserver = new ResizeObserver(resizeCard)
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }

      // Also listen to window resize
      window.addEventListener('resize', resizeCard)

      return () => {
        resizeObserver.disconnect()
        window.removeEventListener('resize', resizeCard)
      }
    }, [zoom])

    // Calculate scale factor for fonts and elements based on current size vs original
    const scaleFactor = cardDimensions.width / DIMENSION_A5.width

    // Helper function to calculate scaled position value
    const scaleValue = (value: string | number | undefined, dimension: 'width' | 'height') => {
      if (value === undefined) return undefined
      if (typeof value === 'string') {
        if (value.endsWith('%')) return value
        return value
      }
      const baseDimension = dimension === 'width' ? cardDimensions.width : cardDimensions.height
      return `${(value / (dimension === 'width' ? DIMENSION_A5.width : DIMENSION_A5.height)) * baseDimension}px`
    }

    // Helper function to get styling by ID
    const getStyling = (stylingId: string): DrawingStyling | undefined => {
      return template.stylings.find(s => s.id === stylingId)
    }

    // Helper function to apply stylings to an element
    const getElementStyles = (element: TemplateElement): React.CSSProperties => {
      const styles: React.CSSProperties = {}

      if (element.stylings) {
        for (const stylingId of element.stylings) {
          const styling = getStyling(stylingId)
          if (styling) {
            if (styling.target === 'text' && element.type === 'text') {
              styles.color = styling.color
            }
            // Can add more styling properties here as needed
          }
        }
      }

      return styles
    }

    // Render a single element based on its type
    const renderElement = (element: TemplateElement) => {
      const commonStyles: React.CSSProperties = {
        position: 'absolute',
        top: scaleValue(element.position.top, 'height'),
        left: scaleValue(element.position.left, 'width'),
        width: scaleValue(element.position.width, 'width'),
        height: scaleValue(element.position.height, 'height'),
        ...element.style,
      }

      const appliedStylings = getElementStyles(element)
      const elementOverride = elementOverrides?.elements.find(elementOverride => elementOverride.elementId == element.id)

      switch (element.type) {
        case 'text': {
          // Get the actual font CSS from the font ID
          const fontDef = element.fontFamily ? getFontDefinitionById(element.fontFamily) : null
          const fontFamilyCSS = fontDef?.css || 'sans-serif'

          return (
            <div
              contentEditable={true}
              key={element.id}
              style={{
                ...commonStyles,
                fontFamily: fontFamilyCSS,
                fontSize: `${element.fontSize * scaleFactor}px`,
                color: appliedStylings.color || element.color,
                textAlign: element.textAlign,
                letterSpacing: element.letterSpacing ? `${element.letterSpacing * scaleFactor}px` : undefined,
                lineHeight: element.lineHeight || 1.2,
                whiteSpace: 'pre-line', // This enables \n to create line breaks
              }}
            >
              {elementOverride?.text || element.content}
            </div>
          )
        }

        case 'image':
          return (
            <img
              crossOrigin='anonymous'
              key={element.id}
              src={elementOverride?.imgSrc || element.src}
              alt={element.alt || ''}
              style={{
                ...commonStyles,
                objectFit: 'contain',
              }}
            />
          )

        case 'rect':
          return (
            <div
              key={element.id}
              style={{
                ...commonStyles,
                backgroundColor: element.backgroundColor || 'transparent',
              }}
            />
          )

        default:
          return null
      }
    }

    return (
      <div className="w-full h-full flex justify-center items-center overflow-hidden" ref={containerRef}>
        <div
          ref={cardRef}
          className="relative shadow-lg bg-white"
          style={{
            width: `${cardDimensions.width}px`,
            height: `${cardDimensions.height}px`,
          }}
        >
          {/* Background from template */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={template.background.type === 'image' ? {
              backgroundImage: `url('${template.background.value}')`,
            } : {
              backgroundColor: template.background.value,
            }}
          />

          {/* Render all template elements dynamically */}
          {template.elements.map(renderElement)}
        </div>
      </div>
    )
  }
)

DrawingCanvas.displayName = 'DrawingCanvas'

export default DrawingCanvas
